import React, { useEffect, useRef } from 'react';
import './styles.css'; // Import the prefixed CSS

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Project Management Content) ---
import {
    // Core Concepts & General
    faRulerCombined,
    faClipboardList,    // Project Management / Planning
    faHardHat,          // Construction / Execution
    faBuilding,         // Facility / Infrastructure
    faUserTie,          // Expertise / Consultant / Specialist PM
    faDollarSign,       // Cost Control / Budget
    faClock,            // Time Management / Efficiency / Timely Delivery
    faCheckCircle,      // Compliance / Quality Standards / Approval
    faEye,              // Oversight / Monitoring
    faShoppingCart,     // Procurement / Material Coordination
    faExclamationTriangle, // Risk Management
    faSearchPlus,       // Quality Assurance / Audits
    faDraftingCompass,  // Design / Layout
    faFileSignature,    // Permitting / Legal
    faHandshake,        // Contractor Selection / Collaboration
    faKey,              // Handover / Completion
    faTasks,            // Comprehensive Support / Services
    faAward,            // Quality Assurance Benefit
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faExpandArrowsAlt, faTools, faRoute, faChartLine, faLaptopCode, faTrophy, faWarehouse,
    faSearch, faComments, faCog, faUserCheck, faGlobe, faLeaf, faRocket, faShieldAlt,
    faPalette, faTree, faDirections, faRulerCombined, faBiohazard, faMicroscope
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Why Choose Cliniqor?
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Core Services
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Step-by-Step Process
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Benefits of Choosing

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for project management/construction
    const heroBgImage = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Construction site/planning image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Hospital Project Management Consulting</h1>
            <p className="cliniqor-sub-heading">
              Comprehensive Solutions by Cliniqor for Successful Hospital Construction
            </p>
            <p className="cliniqor-intro-text">
              Expert consultants managing complex healthcare facility construction. Our dedicated team ensures smooth, efficient, on-time completion, focusing on budget, quality, and compliance from concept to handover.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- INTRO SECTION (Optional Elaboration) --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
             <div className="cliniqor-intro-grid">
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <h3>Guidance Through Every Step</h3>
                     <p>Whether building new, expanding, or renovating, Cliniqor provides expert guidance, risk management, and seamless coordination. Our services ensure your hospital is constructed to provide excellent patient care for years to come.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- WHY CHOOSE CLINIQOR? (Features Section - 4 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Why Choose Cliniqor for Your Hospital Project Management Consulting?</h2>
              <p>Critical factors ensuring project success.</p>
              {/* --- END NEW SECTION TITLE --- */}
            </div>
            <div className="cliniqor-features-grid"> {/* Grid structure maintained (4 items fit well) */}
              {/* --- NEW FEATURE CARDS --- */}
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                    <h4>1. Specialized Healthcare Expertise</h4>
                    <p>Understanding unique design, regulatory, and operational requirements of hospitals.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                    <h4>2. Cost Control & Budget Management</h4>
                    <p>Ensuring projects are built within financial parameters, minimizing waste, maximizing value.</p>
                </div>
              </div>
              <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClock} /></div>
                    <h4>3. Time Management & Efficiency</h4>
                    <p>Meeting timelines, achieving milestones, and mitigating potential delays proactively.</p>
                </div>
              </div>
               <div className="cliniqor-feature-card cliniqor-fade-in-up">
                 <div className="cliniqor-feature-card-content">
                    <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <h4>4. Compliance & Quality Standards</h4>
                    <p>Adhering to accreditation standards, safety codes, and regulations for functionality.</p>
                </div>
              </div>
              {/* --- END NEW FEATURE CARDS --- */}
            </div>
          </div>
        </section>

        {/* --- CORE SERVICES (Detailed Planning Section - 6 items) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>Core Services in Hospital Project Management Consulting</h2>
               <p>A holistic approach managing every aspect from planning to final walk-through.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for Core Services */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faClipboardList} /></div>
                    <div className="cliniqor-content"><h4>1. Project Initiation and Planning</h4> <p>Needs assessment, feasibility study, preliminary design, risk analysis, budget estimation.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faEye} /></div>
                    <div className="cliniqor-content"><h4>2. Design and Construction Oversight</h4> <p>Design review/approval, contractor selection, project scheduling, coordination, supervision.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faShoppingCart} /></div>
                    <div className="cliniqor-content"><h4>3. Procurement Management & Material Coordination</h4> <p>Supplier identification, cost negotiation, inventory management, quality assurance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faBuilding} /></div>
                    <div className="cliniqor-content"><h4>4. On-Site Project Management</h4> <p>Daily monitoring, vendor/contractor coordination, progress tracking, quality control, compliance.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                    <div className="cliniqor-content"><h4>5. Risk Management and Issue Resolution</h4> <p>Proactive risk identification, contingency planning, resolving legal/regulatory challenges, insurance.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSearchPlus} /></div> {/* Using search/magnify for QA */}
                    <div className="cliniqor-content"><h4>6. Quality Assurance and Compliance Audits</h4> <p>Construction audits, compliance checks (codes, regulations), safety management.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- STEP-BY-STEP PROCESS (Reusing Detailed Planning Layout) --- */}
        <section className="cliniqor-section cliniqor-section-bg-light cliniqor-detailed-planning-section"> {/* Use light background for variation */}
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Hospital Design and Project Delivery: A Step-by-Step Process</h2>
                 <p>A structured approach ensuring clarity, transparency, and high-quality outcomes.</p>
              </div>
              {/* Using Planning Items for Process Steps */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faDraftingCompass} /></div>
                    <div className="cliniqor-content"><h4>1. Preliminary Design and Planning</h4> <p>Comprehensive design reviews and conceptual planning aligning with healthcare goals.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faRulerCombined} /></div> {/* Icon for detailed design */}
                    <div className="cliniqor-content"><h4>2. Detailed Project Design</h4> <p>Finalizing layouts for patient rooms, ER, labs, and essential departments with your team.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faFileSignature} /></div>
                    <div className="cliniqor-content"><h4>3. Permitting and Compliance</h4> <p>Handling permitting processes and ensuring compliance with laws, codes, and regulations.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHandshake} /></div>
                    <div className="cliniqor-content"><h4>4. Contractor Selection and Procurement</h4> <p>Assisting in selecting the right contractors and suppliers for materials and equipment.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faHardHat} /></div>
                    <div className="cliniqor-content"><h4>5. Construction and Implementation</h4> <p>Supervising construction, maintaining high standards, and adhering to timelines.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faKey} /></div>
                    <div className="cliniqor-content"><h4>6. Handover and Post-Construction Support</h4> <p>Ensuring operational readiness, providing support, staff training, and maintenance planning.</p> </div>
                 </div>
            </div>
           </div>
        </section>

        {/* --- BENEFITS OF CHOOSING (Reusing Features Layout - 5 items) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-texture"> {/* Use texture background */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Benefits of Choosing Cliniqor for Hospital Project Management</h2>
                 <p>Investing in the successful execution of your construction project.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Benefits --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUserTie} /></div>
                            <h4>Expert Project Management</h4>
                            <p>Years of healthcare construction experience ensuring efficient execution.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faDollarSign} /></div>
                            <h4>Cost-Effective Solutions</h4>
                            <p>Optimizing resources and reducing unnecessary expenses to stay within budget.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faAward} /></div> {/* Award for Quality */}
                            <h4>Quality Assurance</h4>
                            <p>Building to the highest standards, ensuring compliance and operational needs.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faClock} /></div>
                            <h4>Timely Delivery</h4>
                            <p>Ensuring your hospital opens on time to serve the community without delay.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTasks} /></div>
                            <h4>Comprehensive Support</h4>
                            <p>Continuous support from planning to completion, addressing challenges proactively.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h3>Expert Hospital Project Management Consulting Services</h3>
          <p>Planning a new hospital or expanding an existing facility? Cliniqor guides you every step of the way. Our tailored services ensure your project is completed on time, within budget, and to the highest quality standards. Get in touch today!</p>
          <a href="#contact" className="cliniqor-cta-button">Turn Your Vision Into Reality</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;